// src/components/product/AddToCartModal.jsx
import React, { useState, useEffect } from 'react'; // Added useEffect to import
import Modal from '../common/Modal';
import Button from '../common/Button';
import Input from '../common/Input';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { formatCurrency, isValidCustomName } from '../../utils/helpers';
import { CUSTOM_NAME_CONFIG, getSizeLabel, sortBySizeOrder } from '../../utils/constants';

const AddToCartModal = ({ isOpen, onClose, product }) => {
  const { addToCart, loading } = useCart();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  
  // Form state
  const [quantity, setQuantity] = useState(1);
  const [customName, setCustomName] = useState('');
  const [passingYear, setPassingYear] = useState('');
  const [errors, setErrors] = useState({});
  const [selectedSize, setSelectedSize] = useState(null);

  // 1. Helper variable for the selected size data
  const selectedSizeData = product.availableSizes?.find(s => s.size === selectedSize);

  // 2. Helper variable for display price
  const displayPrice = selectedSizeData 
    ? selectedSizeData.finalPrice 
    : ((100 - product.discount) / 100) * product.startingPrice;

  // ⭐ FIX: Sync quantity when size changes (e.g. if switching to a size with lower stock)
  useEffect(() => {
    if (selectedSizeData) {
      const maxStock = selectedSizeData.stockQuantity;
      if (quantity > maxStock) {
        setQuantity(maxStock > 0 ? maxStock : 1);
      }
    }
  }, [selectedSize, selectedSizeData]);

  const handleClose = () => {
    setSelectedSize(null);
    setQuantity(1);
    setCustomName('');
    setPassingYear('');
    setErrors({});
    onClose();
  };
  
  const validateForm = () => {
    const newErrors = {};
    const maxStock = selectedSizeData?.stockQuantity || 0;
    
    if (!selectedSize) {
      newErrors.size = 'Please select a size';
    }
    
    if (quantity < 1) {
      newErrors.quantity = 'Quantity must be at least 1';
    }
    
    // ⭐ FIX: Correct stock comparison
    if (selectedSize && quantity > maxStock) {
      newErrors.quantity = `Only ${maxStock} items available for this size`;
    }

    // NEW: Validation for Passing Year
    if (passingYear !== '') {
    const yearNum = parseInt(passingYear);
    if (yearNum < 1900 || yearNum > 3000) {
      newErrors.passingYear = 'Please enter a valid year (1900-3000)';
    }
  }
    
    if (customName && !isValidCustomName(customName)) {
      newErrors.customName = CUSTOM_NAME_CONFIG.HELPER_TEXT;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleAddToCart = async () => {
    if (!isAuthenticated) {
      alert('Please login to add items to cart');
      navigate('/login');
      handleClose();
      return;
    }
    
    if (!validateForm()) return;
    
    try {
      await addToCart({
        productId: product.id,
        size: selectedSize,
        quantity: quantity,
        customName: customName.trim() || null,
        passingYear: passingYear !== '' ? parseInt(passingYear) : null,
      });
      
      alert('Item added to cart!');
      handleClose();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to add item to cart');
    }
  };
  
  const subtotal = displayPrice * quantity;
  
  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={`Add to Cart - ${product.name}`}
      size="md"
      footer={
        <>
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button onClick={handleAddToCart} loading={loading} disabled={selectedSizeData?.stockQuantity === 0}>
            {selectedSizeData?.stockQuantity === 0 ? 'Out of Stock' : `Add to Cart - ${formatCurrency(subtotal)}`}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {/* Product Preview */}
        <div className="flex gap-4 p-4 bg-gray-50 rounded-lg">
          <img
            src={product.imageUrls?.[0] || '/placeholder-product.png'}
            alt={product.name}
            className="w-20 h-20 object-cover rounded-md"
          />
          <div>
            <h4 className="font-semibold text-gray-900">{product.name}</h4>
            <p className="text-sm text-gray-600">{product.college?.name}</p>
            <p className="text-lg font-bold text-blue-600 mt-1">
              {formatCurrency(displayPrice)}
            </p>
          </div>
        </div>
        
        {/* Size Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select Size <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-5 gap-2">
            {sortBySizeOrder(product.availableSizes).map((ps) => (
              <button
                key={ps.id}
                onClick={() => setSelectedSize(ps.size)}
                disabled={ps.stockQuantity === 0}
                className={`
                  py-2 px-4 border-2 rounded-lg font-medium transition-all relative
                  ${ps.stockQuantity === 0 ? 'opacity-50 cursor-not-allowed bg-gray-100 border-gray-200' : ''}
                  ${selectedSize === ps.size
                    ? 'border-blue-600 bg-blue-600 text-white shadow-sm'
                    : ps.stockQuantity > 0 ? 'border-gray-300 text-gray-700 hover:border-gray-400 hover:bg-gray-50' : ''
                  }
                `}
              >
                {getSizeLabel(ps.size)}
                {ps.stockQuantity === 0 && <span className="absolute -top-2 -right-1 text-[10px] bg-red-500 text-white px-1 rounded">0</span>}
              </button>
            ))}
          </div>
          {errors.size && <p className="mt-1 text-sm text-red-600">{errors.size}</p>}
        </div>
        
        {/* Quantity Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Quantity <span className="text-red-500">*</span>
          </label>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setQuantity(Math.max(1, quantity - 1))}
              className="w-10 h-10 border-2 border-gray-300 rounded-lg hover:border-gray-400"
            >
              −
            </button>
            <input
              type="number"
              value={quantity}
              readOnly
              className="w-20 text-center border-2 border-gray-300 rounded-lg py-2 focus:outline-none"
            />
            <button
              type="button"
              onClick={() => setQuantity(prev => Math.min(selectedSizeData?.stockQuantity || 1, prev + 1))}
              disabled={!selectedSize || quantity >= (selectedSizeData?.stockQuantity || 0)}
              className={`w-10 h-10 border-2 rounded-lg transition-colors ${
                (!selectedSize || quantity >= (selectedSizeData?.stockQuantity || 0))
                  ? 'border-gray-200 text-gray-400 cursor-not-allowed'
                  : 'border-gray-300 hover:border-gray-400'
              }`}
            >
              +
            </button>
            <span className="text-sm text-gray-600">
              {selectedSize ? `${selectedSizeData?.stockQuantity} Units available` : "Select a size"}
            </span>
          </div>
          {errors.quantity && <p className="mt-1 text-sm text-red-600">{errors.quantity}</p>}
        </div>
        
        {/* Custom Name Input */}
        <div className="border-t pt-4">
          <Input
            label="Custom Name for Printing (Optional)"
            value={customName}
            onChange={(e) => setCustomName(e.target.value.toUpperCase())}
            placeholder="e.g. NITIN"
            helperText={CUSTOM_NAME_CONFIG.HELPER_TEXT}
            error={errors.customName}
            maxLength={CUSTOM_NAME_CONFIG.MAX_LENGTH}
          />
        </div>
        <div className="border-t pt-4">
    <Input
      type="number"
      label="Graduation Year (Optional)"
      value={passingYear}
      onChange={(e) => setPassingYear(e.target.value)}
      placeholder="e.g. 2026"
      error={errors.passingYear}
      min="1900"
      max="3000"
    />
  </div>
      </div>
    </Modal>
  );
};

export default AddToCartModal;